import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setIsShowingUserSheet } from 'store/appSlice';
import { styled } from '@mui/material/styles';
import { selectAvatar } from 'store/userSlice';
import { hasValue } from 'swipeclock-frontend';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export default function UserAvatar() {
  const dispatch = useDispatch();
  const user = useSelector(selectAvatar);
  const { name, initials } = useAvatarMaker(user);

  return (
    <>
      {hasValue(name) && (
        <StyledButton
          onClick={() =>
            dispatch(setIsShowingUserSheet({ showing: true, name }))
          }
        >
          <StyledAvatar alt={name}>{initials}</StyledAvatar>
          <StyledName>{name}</StyledName>
        </StyledButton>
      )}
    </>
  );
}

export function UserAvatarForDrawer() {
  const user = useSelector(selectAvatar);
  const { name, initials } = useAvatarMaker(user);
  return hasValue(name) ? (
    <Stack direction="row" alignItems="center" gap={1}>
      <StyledAvatarForDrawer alt={name}>{initials}</StyledAvatarForDrawer>
    </Stack>
  ) : null;
}

// Helper Hooks and Functions

function useAvatarMaker({ employeeName, loginInfo }) {
  // Helper function to create initials from a name.
  const makeInitials = (name) => {
    // Even if there is a first name with spaces,
    // we're just going to create initials using the first word (in name)
    // and last word (in name) and assume those are the right initials.
    const parts = name.toUpperCase().split(' ');
    const firstName = parts[0];

    // If we're only using username as "name" then we should
    // just show one initial.
    if (parts.length === 1) {
      return firstName[0];
    }

    const lastName = parts[parts.length - 1];

    return firstName[0] + lastName[0];
  };

  // Helper function to create an object with name and initials.
  const getNameAndInitials = (name) => {
    if (!hasValue(name)) return undefined;
    const initials = makeInitials(name);
    return { name, initials };
  };

  // Real logic starts here.
  const avatar = useMemo(() => {
    if (hasValue(employeeName)) {
      return getNameAndInitials(employeeName);
    }

    if (
      hasValue(loginInfo?.FirstName) &&
      hasValue(loginInfo?.LastName) &&
      loginInfo.FirstName !== 'null' &&
      loginInfo.LastName !== 'null'
    ) {
      const fullName = `${loginInfo.FirstName} ${loginInfo.LastName}`;
      return getNameAndInitials(fullName);
    }

    if (hasValue(loginInfo) && hasValue(loginInfo.Login)) {
      return getNameAndInitials(loginInfo.Login);
    }

    // Fallback case
    return { name: null, initials: null };
  }, [employeeName, loginInfo?.FirstName, loginInfo?.LastName]);
  return avatar;
}

// Styled Components

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.custom.navyBlue.main,
  color: theme.palette.primary.contrastText,
  border: `2px solid ${theme.palette.primary.contrastText}`,
  width: 32,
  height: 32,
  fontSize: '0.87rem',
  fontWeight: theme.typography.fontWeightLight,
}));

const StyledAvatarForDrawer = styled(Avatar)(({ theme }) => ({
  color: theme.palette.custom.navyBlue.main,
  backgroundColor: theme.palette.common.white,
  border: `2px solid ${theme.palette.custom.navyBlue.main}`,
  fontWeight: theme.typography.fontWeighLight,
}));

const StyledName = styled((props) => <Typography variant="body1" {...props} />)(
  ({ theme }) => ({
    color: theme.palette.primary.contrastText,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  })
);

const StyledButton = styled((props) => (
  <Stack
    component={ButtonBase}
    direction="row"
    alignItems="center"
    gap={1}
    {...props}
  />
))(({ theme }) => ({
  '&: hover div': {
    backgroundColor: theme.palette.custom.navyBlue.main,
    color: theme.palette.secondary.light,
    borderColor: theme.palette.secondary.light,
  },
  '&:hover p': {
    color: theme.palette.secondary.light,
  },
}));
